import type { ItEmailPublic, ItEmailStatus, SessionUser } from '@accounts/shared';
import { type ReactNode, useState } from 'react';

import { STR } from '../../../../i18n';
import { formatDate, formatDateTime, relativeTime } from '../../../../lib/format';
import { Callout, Card, CardHeader, Dot, type DotTone, MasterDetailSkeleton } from '../../../../ui/Primitives';
import {
  buttonStyle,
  detailColumnStyle,
  detailPaneStyle,
  eyebrowStyle,
  listContainerStyle,
  listPaneHeaderStyle,
  listPaneStyle,
  listRowStyle,
  masterDetailStyle,
  monoStyle,
  primaryButtonStyle,
  quietButtonStyle,
  rowActionStyle,
} from '../../../../ui/styles';
import { useRoleQueue } from '../queue/useRoleQueue';

import { type ItFlowStart, ItEmailFlow } from './ItEmailFlow';
import { pickable } from './picker';
import { toneBar, toneOfAction } from './tone';
import { ColouredEmail, ToneLegend, ToneWord } from './ToneParts';
import { useItEmailActions, useItEmails, useItStanding } from './useItEmails';

/** Drafts first, then waiting, then finished, then taken back; newest first in each. */
const RANK: Record<ItEmailStatus, number> = { draft: 0, sent: 1, completed: 2, taken_back: 3 };

/** A sent email with nothing open is finished, whatever the server last called it. */
function stateOf(email: ItEmailPublic): ItEmailStatus {
  return email.status === 'sent' && email.openItemCount === 0 ? 'completed' : email.status;
}

function dotOf(email: ItEmailPublic): DotTone {
  const state = stateOf(email);
  if (state === 'sent') return email.overdue ? 'bad' : 'warn';
  return state === 'completed' ? 'ok' : 'neutral';
}

/**
 * IT EMAILS (spec row 15; FINAL demo R3-2): the IT step as its own page. Every email to IT,
 * what it asked for and what is left: drafts to finish and send, emails waiting on IT (with
 * IT confirmed all, Take back and each item's Mark granted / removed), and the finished and
 * taken-back ones as a record. Administrators and HR both see every email (SIR-7690).
 */
export function ItEmailsPanel({ user }: { user: SessionUser }) {
  const emails = useItEmails();
  const queue = useRoleQueue(user);
  const it = useItStanding();
  const actions = useItEmailActions();
  const [picked, setPicked] = useState<string | null>(null);
  const [flow, setFlow] = useState<ItFlowStart | null>(null);

  if (emails.isLoading) return <MasterDetailSkeleton />;
  if (emails.isError || emails.data === undefined) {
    return (
      <div style={{ padding: '20px 24px' }}>
        <Callout tone="danger" role="alert">
          {STR.errors.couldNotLoad}
        </Callout>
      </div>
    );
  }

  const overdueDays = emails.data.meta.overdueAfterDays;
  const list = [...emails.data.data]
    .filter((email) => email.items.length > 0 || email.sentAt !== null)
    .sort(
      (a, b) =>
        RANK[stateOf(a)] - RANK[stateOf(b)] ||
        Date.parse(b.sentAt ?? b.createdAt) - Date.parse(a.sentAt ?? a.createdAt),
    );
  const selected = list.find((email) => email.id === picked) ?? list[0] ?? null;
  const open = pickable(queue.items, it.standing);
  const busy = actions.isPending;

  const waiting = list.filter((email) => stateOf(email) === 'sent');
  const head = STR.itEmails.pageHead(
    waiting.length,
    waiting.reduce((sum, email) => sum + email.openItemCount, 0),
    list.filter((email) => email.status === 'draft').length,
    list.filter((email) => stateOf(email) === 'completed').length,
  );

  const newEmail = (primary: boolean) => (
    <button
      type="button"
      className={primary ? 'prim' : 'ghost'}
      style={primary ? primaryButtonStyle : { ...quietButtonStyle, width: '100%' }}
      disabled={open.length === 0}
      onClick={() => {
        setFlow({ kind: 'pick', initial: new Set() });
      }}
    >
      {STR.itEmails.newEmailOpen(open.length)}
    </button>
  );

  let detail: ReactNode;
  if (selected === null) {
    detail = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 620 }}>
        <div style={{ fontSize: 19, fontWeight: 650 }}>{STR.itEmails.emptyTitle}</div>
        <div className="tx-subtle" style={{ fontSize: 13, lineHeight: 1.6 }}>
          {STR.itEmails.emptyBody}
        </div>
        <div>{newEmail(true)}</div>
      </div>
    );
  } else {
    const state = stateOf(selected);
    const late = state === 'sent' && selected.overdue;
    const sub =
      selected.sentAt !== null
        ? STR.itEmails.detailSent(
            formatDateTime(selected.sentAt),
            selected.sentBy?.name ?? selected.createdBy.name,
            relativeTime(selected.sentAt),
          )
        : selected.takenBackAt !== null
          ? STR.itEmails.detailTakenBack(
              formatDateTime(selected.takenBackAt),
              selected.takenBackBy?.name ?? selected.createdBy.name,
            )
          : STR.itEmails.detailSaved(formatDateTime(selected.createdAt), selected.createdBy.name);

    const openEmail = () => {
      setFlow({ kind: 'email', email: selected });
    };
    let acts: ReactNode;
    if (state === 'draft') {
      acts = (
        <>
          <button type="button" className="prim" style={primaryButtonStyle} onClick={openEmail}>
            {STR.itEmails.openAndSend}
          </button>
          <button
            type="button"
            className="tbtn tbtn-danger"
            style={buttonStyle}
            disabled={busy}
            onClick={() => {
              actions.mutate({ kind: 'takeBack', email: selected });
            }}
          >
            {STR.itEmails.discard}
          </button>
        </>
      );
    } else if (state === 'sent') {
      acts = (
        <>
          <button type="button" className="ghost" style={buttonStyle} onClick={openEmail}>
            {STR.itEmails.copyAgain}
          </button>
          <button
            type="button"
            className="prim"
            style={primaryButtonStyle}
            disabled={busy}
            onClick={() => {
              actions.mutate({ kind: 'confirmAll', email: selected });
            }}
          >
            {STR.itEmails.confirmAll(selected.openItemCount)}
          </button>
          <button
            type="button"
            className="tbtn"
            style={buttonStyle}
            disabled={busy}
            onClick={() => {
              actions.mutate({ kind: 'takeBack', email: selected });
            }}
          >
            {STR.itEmails.takeBack}
          </button>
        </>
      );
    } else {
      acts = (
        <button type="button" className="ghost" style={buttonStyle} onClick={openEmail}>
          {STR.itEmails.showText}
        </button>
      );
    }

    detail = (
      <div style={detailColumnStyle}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 19, fontWeight: 650, letterSpacing: '-.015em' }}>
              {STR.itEmails.state[state]}
            </div>
            <div className="tx-subtle" style={{ fontSize: 12.5, marginTop: 3 }}>
              {sub}
              {late ? (
                <>
                  {'. '}
                  <b className="tx-danger-strong">{STR.itEmails.detailOverdue(overdueDays)}</b>
                </>
              ) : (
                '.'
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{acts}</div>
        </div>

        <Card>
          <CardHeader
            title={STR.itEmails.asksFor}
            aside={
              <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
                {STR.itEmails.asksCount(
                  selected.items.length,
                  state === 'sent' ? selected.openItemCount : null,
                )}
              </span>
            }
          />
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {selected.items.map((item) => {
              const tone = toneOfAction(item.action);
              const live = item.status === 'open' && state === 'sent';
              return (
                <li
                  key={item.id}
                  className="row"
                  {...(item.status === 'open' ? {} : { 'data-off': '' })}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '64px minmax(120px,.9fr) minmax(140px,1fr) minmax(0,1fr) 96px auto',
                    gap: 12,
                    alignItems: 'center',
                    padding: '9px 18px',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 12.5,
                    boxShadow: toneBar(tone),
                  }}
                >
                  <ToneWord tone={tone} />
                  <span style={{ fontWeight: 650, minWidth: 0 }}>{item.user.name}</span>
                  <span style={{ minWidth: 0 }}>{item.system.name}</span>
                  <span style={{ minWidth: 0 }}>
                    {item.login === null ? (
                      <span className="tx-subtle">{STR.accessCommon.noLogin}</span>
                    ) : (
                      <span style={{ ...monoStyle, fontSize: 12 }}>{item.login}</span>
                    )}
                  </span>
                  <span className="tx-subtle" style={{ fontSize: 12 }}>
                    {STR.itEmails.itemStatus[item.status]}
                  </span>
                  <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {live ? (
                      <button
                        type="button"
                        className="tbtn"
                        style={rowActionStyle}
                        disabled={busy}
                        onClick={() => {
                          actions.mutate({
                            kind: 'item',
                            email: selected,
                            itemId: item.id,
                            action: item.action,
                          });
                        }}
                      >
                        {item.action === 'set_up'
                          ? STR.access.queue.markGranted
                          : STR.access.queue.markRemoved}
                      </button>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card>
          <CardHeader
            title={STR.itEmails.theEmail}
            aside={
              <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
                {selected.sentAt === null ? STR.itEmails.asSaved : STR.itEmails.asSent}
              </span>
            }
          />
          <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ToneLegend />
            <ColouredEmail text={selected.text} maxHeight="none" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={masterDetailStyle}>
      <div style={listPaneStyle}>
        <div style={listPaneHeaderStyle}>
          <div className="tx-subtle" style={eyebrowStyle}>
            {head}
          </div>
          {newEmail(false)}
        </div>
        <ul style={listContainerStyle} aria-label={STR.itEmails.emailsLabel}>
          {list.map((email) => {
            const state = stateOf(email);
            return (
              <li key={email.id} {...(state === 'taken_back' ? { 'data-off': '' } : {})}>
                <button
                  type="button"
                  className="li"
                  aria-current={selected?.id === email.id}
                  onClick={() => {
                    setPicked(email.id);
                  }}
                  style={listRowStyle}
                >
                  <Dot tone={dotOf(email)} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span className="nm" style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                      {STR.itEmails.state[state]}
                    </span>
                    <span
                      className="tx-subtle"
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {email.sentAt === null
                        ? STR.itEmails.listSaved(formatDate(email.createdAt))
                        : STR.itEmails.listSent(formatDate(email.sentAt))}
                      {', '}
                      {STR.itEmails.listItems(email.items.length)}
                      {state === 'sent' ? `, ${STR.itEmails.listOpen(email.openItemCount)}` : null}
                      {state === 'sent' && email.overdue ? (
                        <>
                          {', '}
                          <b className="tx-danger-strong">{STR.itEmails.overdue}</b>
                        </>
                      ) : null}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div style={detailPaneStyle}>{detail}</div>

      {flow === null ? null : (
        <ItEmailFlow
          key={flow.kind === 'email' ? flow.email.id : 'pick'}
          start={flow}
          pickable={open}
          queueItems={queue.items}
          onClose={() => {
            setFlow(null);
          }}
        />
      )}
    </div>
  );
}
